import useAuthStore from "@/stores/auth";
import { Bug } from "lucide-react";
import { cn } from "@/lib/utils";

interface DebugToolTriggerProps {
  onClick: () => void;
}

export function DebugToolTrigger({ onClick }: DebugToolTriggerProps) {
  // Check for potential issues
  const currentTeam = useAuthStore((state) => state.currentTeam);
  const permissions = useAuthStore((state) => state.permissions);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const permissionsLoading = useAuthStore((state) => state.permissionsLoading);
  const teamsLoading = useAuthStore((state) => state.teamsLoading);
  const token = useAuthStore((state) => state.token);

  // Detect potential issues
  const hasIssues = (() => {
    // Not hydrated yet
    if (!hasHydrated) return true;

    // No team selected (and not loading)
    if (!currentTeam && !teamsLoading) return true;

    // No permissions loaded (and not loading)
    const permCount = Object.keys(permissions).filter(k => k !== "_hasAllPermissions").length;
    if (permCount === 0 && !permissionsLoading && !permissions._hasAllPermissions) return true;

    // Token expired
    if (token) {
      try {
        const parts = token.split(".");
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) return true;
        }
      } catch {
        // Invalid token format
        return true;
      }
    }

    return false;
  })();

  return (
    <button
      onClick={onClick}
      className={cn(
        "fixed bottom-4 right-4 z-[9999]",
        "flex items-center justify-center",
        "w-12 h-12 rounded-full",
        "bg-background/80 backdrop-blur-md",
        "border border-border/50",
        "shadow-lg shadow-black/10 dark:shadow-black/30",
        "hover:bg-background hover:scale-105",
        "transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      )}
      title="Open Debug Tool (Ctrl+Shift+D)"
    >
      <Bug className="h-5 w-5 text-foreground" />

      {/* Issue indicator dot */}
      {hasIssues && (
        <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
          <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-destructive" />
        </span>
      )}
    </button>
  );
}
